/**
 * CRM Kanban board — the Lead Inbox's Board view. One column per lifecycle
 * stage (funnel order); each column independently pages through
 * GET /api/crm/contacts?lifecycle=<stage> using the inbox's current filters
 * (passed down as props) with a per-column Load more (offset tracked as rows
 * actually received from the server, so drag moves don't skew paging). Archived
 * leads are hidden by default (recruiters archive once a lead is scheduled) but
 * the inbox's "Show archived" toggle folds them back into the columns. Cards
 * open the contact
 * detail; on desktop they can be dragged between columns, which optimistically
 * moves the card (and adjusts the two column counts) then PATCHes
 * lifecycle_status — reverting and surfacing an error on failure.
 *
 * SCOPED TO ONE STUDY, THE DRAG MEANS THAT STUDY (multi-study lead lists, plan
 * §6.4). When the inbox filters to a single CRM study the parent hands down
 * `scopedStudy`, and a drop on a lead who is on that study's list routes through
 * PATCH /contacts/:id/studies/:studyId instead of the contact-level PATCH — it
 * moves the lead's status ON THAT STUDY and leaves the other lists alone. Any
 * other scope (none, several, a lead-only slug with no crm.studies row, or a
 * lead with no membership for it) keeps the contact-level PATCH.
 *
 * Either PATCH can come back 409 randomized_conflict — the lead is enrolled in
 * another study. That is a question, not a failure: the optimistic move reverts,
 * RandomizedConflictModal asks, and confirming re-sends the same request with
 * randomized_conflict_acknowledged:true. A board-level
 * monotonic request sequence guards every fetch: a filter change bumps the
 * sequence so stale responses (including in-flight Load mores) are dropped.
 * Touch devices can't HTML5-drag — cards still open the detail page where the
 * status can be changed; a hint line below the board covers both paths.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Loader2, MoonStar } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, LifecycleStats, MilestoneKey, contactName } from '../types';
import { LIFECYCLES, FINAL_DISPOSITIONS, STUDY_ATTRIBUTED_STAGES, initials, needsReply, relTime, useTeam } from '../lib';
import { ChannelIcon, StudyChips, RowLink, useRowNav } from './shared';
import MilestoneChip from './MilestoneChip';
import { isActiveSnooze } from './SnoozeButton';
import DispositionReasonModal from './DispositionReasonModal';
import StageStudyConfirmModal from './StageStudyConfirmModal';
import RandomizedConflictModal from './RandomizedConflictModal';
import { Alert } from './ui';

const COL_LIMIT = 25;

/**
 * Per-column the milestone whose key just restates that lifecycle column — on
 * the board it would duplicate the column header, so it's suppressed (the
 * cross-cutting milestones like needs_reply / call_scheduled still show). The
 * mixed-lifecycle list/conversation views don't need this.
 */
const COLUMN_MILESTONE: Record<string, MilestoneKey> = {
  scheduled: 'appt_scheduled',
  qualified_pending_records: 'awaiting_records',
  ready_to_schedule: 'ready_to_schedule',
};

/** The inbox's buildParams-relevant filter state (everything except lifecycle). */
export interface KanbanFilters {
  q: string;
  /** Multi-select filters — empty = any. Sent comma-joined; backend ORs them. */
  eligibility: string[];
  milestone: string[];
  study: string[];
  owner: string[];
  unassigned: boolean;
  /** Include archived leads in the columns (recruiters archive after scheduling). */
  showArchived: boolean;
  /** Reveal leads snoozed to a future time (the board hides them by default). */
  showSnoozed: boolean;
  sort: string;
  /** Search-tab run list scope (the board bridge) — '' = off. */
  list: string;
  listName: string;
  /** Future-study interest list scope — '' = off. */
  interest: string;
  interestName: string;
}

interface KanbanBoardProps {
  filters: KanbanFilters;
  stats: LifecycleStats | null;
  /** Bump to force an immediate refetch of all columns (header Refresh). */
  refreshToken?: number;
  /** Called after a successful drag-move so the parent can refresh stats. */
  onMoved?: () => void;
  /**
   * The ONE CRM study this board is scoped to, resolved by the parent from
   * `filters.study` (which carries slugs — the board cannot turn a slug into a
   * study id, and a lead-only slug has no crm.studies row at all). Non-null
   * only when exactly one filtered slug maps to a real CRM study; that is what
   * makes a drag unambiguous enough to route through the membership PATCH.
   */
  scopedStudy?: { id: string; title: string | null } | null;
}

interface ColState {
  rows: ContactListItem[];
  /**
   * Rows actually received from the server (raw batch lengths, before dedup).
   * Used as the Load more offset — rows.length drifts when drags add/remove
   * rows client-side, which would skip or re-fetch server rows.
   */
  fetchedCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Query params shared by every column (same semantics as the list view). */
function baseParams(f: KanbanFilters): Record<string, string> {
  // The pipeline board is a working view — archived leads drop out of it unless
  // the recruiter flips "Show archived" on (leads are commonly archived once
  // scheduled, so hiding them makes late-stage columns look near-empty).
  const p: Record<string, string> = {};
  if (!f.showArchived) p.hide_archived = 'true';
  // The board is a working view, so a snoozed lead drops out of its column the
  // same way it drops out of the list — otherwise "snooze" would just move the
  // lead one tab over. "Show snoozed" brings them back.
  p.show_snoozed = f.showSnoozed ? 'true' : 'false';
  if (f.q.trim()) p.q = f.q.trim();
  // Multi-select filters: comma-join when present, omit when empty (= any).
  if (f.eligibility.length) p.eligibility = f.eligibility.join(',');
  if (f.milestone.length) p.milestone = f.milestone.join(',');
  if (f.study.length) p.study = f.study.join(',');
  if (f.owner.length) p.owner = f.owner.join(',');
  if (f.unassigned) p.unassigned = 'true';
  if (f.sort) p.sort = f.sort;
  if (f.interest) p.interest = f.interest;
  // Search-run list scope (the Search tab's board bridge).
  if (f.list) p.list = f.list;
  return p;
}

const emptyCols = (loading: boolean): Record<string, ColState> => {
  const out: Record<string, ColState> = {};
  for (const l of LIFECYCLES) {
    out[l.value] = { rows: [], fetchedCount: 0, loading, loadingMore: false, hasMore: false, error: null };
  }
  return out;
};

export default function KanbanBoard({ filters, stats, refreshToken = 0, onMoved, scopedStudy = null }: KanbanBoardProps) {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const { byId } = useTeam();

  const [cols, setCols] = useState<Record<string, ColState>>(() => emptyCols(true));
  // Optimistic per-stage count deltas from drag moves, layered over
  // stats.by_status until the parent delivers refreshed stats.
  const [countAdj, setCountAdj] = useState<Record<string, number>>({});
  const [moveError, setMoveError] = useState<string | null>(null);

  // Drag state: the card being dragged and the column currently hovered.
  const [drag, setDrag] = useState<{ id: string; from: string } | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  // A drop onto a negative-disposition column waits here while the recruiter
  // supplies the required reason (the PATCH 400s without it).
  const [pendingDrop, setPendingDrop] = useState<{ d: { id: string; from: string }; to: string; defaultStudyId: string | null; askStudy: boolean } | null>(null);
  // A drop onto a study-attributed positive column (Screened / Randomized)
  // waits here while the recruiter confirms WHICH study the outcome belongs to
  // (the PATCH 400s without it — pivoted leads must land in the right bucket).
  const [pendingStudyDrop, setPendingStudyDrop] = useState<{ d: { id: string; from: string }; to: string; defaultStudyId: string | null } | null>(null);
  // A drop the randomized guard blocked with a 409: the move is already reverted
  // and the request is stashed here, so confirming re-sends the SAME move with
  // the acknowledge flag. Never auto-retried — it is the recruiter's call.
  const [conflict, setConflict] = useState<{
    d: { id: string; from: string };
    to: string;
    extra: Record<string, string | string[]>;
    randomizedStudyTitle: string | null;
    randomizedAt: string | null;
  } | null>(null);

  // Latest filters for fetches kicked off from callbacks/timeouts.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Board-level monotonic sequence: bumped on every full refetch so any
  // in-flight column response (initial or Load more) from an older filter
  // set is dropped instead of committed.
  const seqRef = useRef(0);

  /**
   * B7 — the scoped study's membership on this card, or undefined. Non-undefined
   * is exactly the condition for routing a drag through the membership PATCH: the
   * board is scoped to ONE CRM study AND this lead is on that study's list, so
   * the drop unambiguously means "their status on that study". Everything else
   * (no scope, several studies, a lead-only slug with no crm.studies row, a lead
   * with no membership for it) is either ambiguous or has nothing per-study to
   * move, and keeps the contact-level PATCH.
   */
  const scopedMembership = (card: ContactListItem | undefined) =>
    scopedStudy && card ? (card.memberships || []).find((m) => m.study_id === scopedStudy.id) : undefined;

  const fetchColumn = useCallback(async (lifecycle: string, offset: number, seq: number, append: boolean) => {
    setCols((prev) => ({
      ...prev,
      [lifecycle]: { ...prev[lifecycle], loading: !append, loadingMore: append, error: null },
    }));
    try {
      const res = await api.get('/api/crm/contacts', {
        params: { ...baseParams(filtersRef.current), lifecycle, limit: COL_LIMIT, offset },
      });
      if (seq !== seqRef.current) return; // stale — a newer filter set owns the board
      const batch: ContactListItem[] = res.data?.contacts || [];
      setCols((prev) => {
        const col = prev[lifecycle];
        let rows: ContactListItem[];
        let fetchedCount: number;
        if (append) {
          // Dedup on append: rows created/moved between pages shift the offset
          // and would repeat page-boundary rows (duplicate React keys).
          const seen = new Set(col.rows.map((r) => r.id));
          rows = [...col.rows, ...batch.filter((b) => !seen.has(b.id))];
          // Count the RAW batch (pre-dedup) — that's what the server paged past.
          fetchedCount = col.fetchedCount + batch.length;
        } else {
          rows = batch;
          fetchedCount = batch.length;
        }
        return {
          ...prev,
          [lifecycle]: {
            rows,
            fetchedCount,
            loading: false,
            loadingMore: false,
            hasMore: batch.length === COL_LIMIT,
            error: null,
          },
        };
      });
    } catch (e: any) {
      if (seq !== seqRef.current) return;
      setCols((prev) => ({
        ...prev,
        [lifecycle]: { ...prev[lifecycle], loading: false, loadingMore: false, error: errMsg(e) },
      }));
    }
  }, []);

  const refetchAll = useCallback(() => {
    const seq = ++seqRef.current;
    setMoveError(null);
    setCountAdj({});
    setCols(emptyCols(true));
    LIFECYCLES.forEach((l) => fetchColumn(l.value, 0, seq, false));
  }, [fetchColumn]);

  // Serialize the effective query params so identical params (e.g. a
  // half-typed answer filter) don't refetch. Text filters arrive per keystroke
  // from the parent, so non-initial filter changes debounce 300ms; a
  // refreshToken bump with unchanged filters refetches immediately.
  const filterKey = useMemo(() => JSON.stringify(baseParams(filters)), [filters]);
  const firstRunRef = useRef(true);
  const lastKeyRef = useRef(filterKey);
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastKeyRef.current = filterKey;
      refetchAll();
      return;
    }
    if (lastKeyRef.current !== filterKey) {
      lastKeyRef.current = filterKey;
      const t = setTimeout(refetchAll, 300);
      return () => clearTimeout(t);
    }
    refetchAll();
  }, [filterKey, refreshToken, refetchAll]);

  // Fresh stats from the parent already include any moves we adjusted for.
  useEffect(() => {
    setCountAdj({});
  }, [stats]);

  /** Apply a move (optimistic, then PATCH) — optionally with a disposition
   *  reason, and optionally acknowledging the randomized guard on a re-send. */
  const performMove = (
    d: { id: string; from: string },
    to: string,
    extra: Record<string, string | string[]> = {},
    acknowledged = false,
  ) => {
    const fromCol = cols[d.from];
    const idx = fromCol ? fromCol.rows.findIndex((r) => r.id === d.id) : -1;
    if (idx < 0) return;
    const card = fromCol.rows[idx];
    const seq = seqRef.current;

    const membership = scopedMembership(card);
    // The membership URL already names the study, so the pin hint the
    // disposition modal collected is dropped: a scoped board's outcome belongs
    // to the study being worked, and primary_study_id is not a field that route
    // accepts anyway.
    const membershipExtra = { ...extra };
    delete membershipExtra.primary_study_id;

    setMoveError(null);
    setCols((prev) => {
      const fromRows = prev[d.from].rows.filter((r) => r.id !== d.id);
      // Dedup-guard the destination: a Load more may already have fetched this
      // card into the target column — skip the optimistic insert if so.
      const toRows = prev[to].rows.some((r) => r.id === d.id)
        ? prev[to].rows
        : [{ ...card, lifecycle_status: to }, ...prev[to].rows];
      return {
        ...prev,
        [d.from]: { ...prev[d.from], rows: fromRows },
        [to]: { ...prev[to], rows: toRows },
      };
    });
    setCountAdj((prev) => ({ ...prev, [d.from]: (prev[d.from] || 0) - 1, [to]: (prev[to] || 0) + 1 }));

    const ack = acknowledged ? { randomized_conflict_acknowledged: true } : {};
    (membership
      ? api.patch(`/api/crm/contacts/${d.id}/studies/${membership.study_id}`, {
        status: to, ...membershipExtra, ...ack,
      })
      : api.patch(`/api/crm/contacts/${d.id}`, { lifecycle_status: to, ...extra, ...ack }))
      .then(() => {
        if (seq !== seqRef.current) {
          // The board refetched while the PATCH was in flight and may have
          // read pre-commit data — re-pull the two affected columns.
          fetchColumn(d.from, 0, seqRef.current, false);
          fetchColumn(to, 0, seqRef.current, false);
        }
        onMoved?.();
      })
      .catch((e: any) => {
        if (seq !== seqRef.current) return; // board refetched since — fresh data is already correct
        setCols((prev) => {
          const toRows = prev[to].rows.filter((r) => r.id !== d.id);
          const fromRows = prev[d.from].rows.filter((r) => r.id !== d.id);
          fromRows.splice(Math.min(idx, fromRows.length), 0, card);
          return {
            ...prev,
            [to]: { ...prev[to], rows: toRows },
            [d.from]: { ...prev[d.from], rows: fromRows },
          };
        });
        setCountAdj((prev) => ({ ...prev, [d.from]: (prev[d.from] || 0) + 1, [to]: (prev[to] || 0) - 1 }));
        // The lead is randomized in another study. Not an error — the guard is
        // asking a question, and the card is already back where it started, so
        // the modal (not a red banner) is the right surface. Confirming re-runs
        // this exact move with the acknowledge flag, which re-applies the
        // optimistic insert from scratch.
        const data = e?.response?.data;
        if (e?.response?.status === 409 && data?.reason === 'randomized_conflict') {
          setConflict({
            d,
            to,
            extra,
            randomizedStudyTitle: data.randomized_study?.title ?? null,
            randomizedAt: data.randomized_at ?? null,
          });
        } else {
          setMoveError(`Couldn't move ${contactName(card)}: ${errMsg(e)}`);
        }
        // A stats refresh mid-flight may have cleared countAdj before the
        // inverse deltas landed — ask the parent for fresh stats to converge.
        onMoved?.();
      });
  };

  /** Drop a dragged card onto a column. Negative dispositions collect a reason
   *  first; study-attributed stages (Screened / Randomized / Screen Failed)
   *  confirm the study the outcome belongs to — unless the board is already
   *  scoped to that study (the card stays put until the modal is confirmed). */
  const onDrop = (to: string) => {
    setDragOver(null);
    const d = drag;
    setDrag(null);
    if (!d || d.from === to) return;
    const card = cols[d.from]?.rows.find((r) => r.id === d.id);
    const defaultStudyId = card?.primary_study_id || null;
    // A scoped drag has already named the study (B7) and the membership route
    // takes it from the URL, so the "which study is this outcome for?" question
    // is answered — asking it anyway would collect a pin the route ignores. The
    // REASON is still required (the route 400s reason_required without it), so
    // the disposition modal still opens, just without its study picker.
    const scoped = Boolean(scopedMembership(card));
    if (FINAL_DISPOSITIONS.includes(to)) {
      setPendingDrop({ d, to, defaultStudyId, askStudy: !scoped && STUDY_ATTRIBUTED_STAGES.includes(to) });
      return;
    }
    if (!scoped && STUDY_ATTRIBUTED_STAGES.includes(to)) {
      setPendingStudyDrop({ d, to, defaultStudyId });
      return;
    }
    performMove(d, to);
  };

  // Any active filter narrows the server result set, so the global stats
  // counts no longer describe what the columns show (sort doesn't narrow).
  const filtersActive =
    filters.unassigned ||
    Boolean(filters.q.trim()) ||
    filters.owner.length > 0 ||
    filters.study.length > 0 ||
    filters.eligibility.length > 0 ||
    filters.milestone.length > 0 ||
    Boolean(filters.list) ||
    Boolean(filters.interest);

  return (
    <div className="space-y-2">
      {pendingDrop && (
        <DispositionReasonModal
          status={pendingDrop.to}
          askStudy={pendingDrop.askStudy}
          defaultStudyId={pendingDrop.defaultStudyId}
          onCancel={() => setPendingDrop(null)}
          onConfirm={(reasonCode, note, studyId, criterionId, interestIds) => {
            const { d, to } = pendingDrop;
            setPendingDrop(null);
            const extra: Record<string, string | string[]> = { reason_code: reasonCode };
            if (note) extra.disposition_note = note;
            if (studyId) extra.primary_study_id = studyId;
            if (criterionId) extra.criterion_id = criterionId;
            // Future-study lists picked in the close-out modal (owner 2026-08-20).
            if (interestIds?.length) extra.interest_category_ids = interestIds;
            performMove(d, to, extra);
          }}
        />
      )}
      {pendingStudyDrop && (
        <StageStudyConfirmModal
          status={pendingStudyDrop.to}
          defaultStudyId={pendingStudyDrop.defaultStudyId}
          onCancel={() => setPendingStudyDrop(null)}
          onConfirm={(studyId) => {
            const { d, to } = pendingStudyDrop;
            setPendingStudyDrop(null);
            performMove(d, to, studyId ? { primary_study_id: studyId } : {});
          }}
        />
      )}
      {conflict && (
        <RandomizedConflictModal
          targetStudyTitle={scopedStudy?.title ?? null}
          randomizedStudyTitle={conflict.randomizedStudyTitle}
          randomizedAt={conflict.randomizedAt}
          onCancel={() => setConflict(null)}
          onConfirm={() => {
            const { d, to, extra } = conflict;
            setConflict(null);
            performMove(d, to, extra, true);
          }}
        />
      )}
      {moveError && (
        <Alert tone="error" icon={false}>{moveError}</Alert>
      )}

      <div className="flex items-start gap-3 overflow-x-auto pb-2">
        {LIFECYCLES.map((l) => {
          const col = cols[l.value];
          // Filtered boards show the column's loaded count ("25+" while more
          // pages remain) — the global stats counts would lie under filters.
          const badge = filtersActive
            ? col.loading
              ? null
              : `${col.rows.length.toLocaleString()}${col.hasMore ? '+' : ''}`
            : stats
              ? Math.max(0, (stats.by_status?.[l.value] ?? 0) + (countAdj[l.value] || 0)).toLocaleString()
              : null;
          const highlighted = drag !== null && dragOver === l.value;
          return (
            <div
              key={l.value}
              onDragOver={(e) => {
                if (!drag) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOver !== l.value) setDragOver(l.value);
              }}
              onDragLeave={(e) => {
                // Ignore leave events into the column's own children.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDragOver((cur) => (cur === l.value ? null : cur));
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                onDrop(l.value);
              }}
              className={`w-[85vw] max-w-[18rem] flex-shrink-0 rounded-xl border bg-gray-50 dark:bg-gray-900/40 lg:w-72 ${
                highlighted ? 'border-primary-400 ring-2 ring-primary-400' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className={`h-2 w-2 flex-shrink-0 rounded-full ${l.dot}`} />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{l.label}</span>
                {badge !== null && (
                  <span className="ml-auto flex-shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {badge}
                  </span>
                )}
              </div>

              <div className="min-h-[6rem] space-y-2 overflow-y-visible px-2 pb-2 lg:max-h-[65vh] lg:overflow-y-auto">
                {col.error && (
                  <Alert tone="error" size="sm" icon={false}>
                    {col.error}
                    <button
                      onClick={() => fetchColumn(l.value, 0, seqRef.current, false)}
                      className="ml-2 font-medium underline transition-colors duration-150 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:hover:text-red-300"
                    >
                      Retry
                    </button>
                  </Alert>
                )}
                {col.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                  </div>
                ) : col.rows.length === 0 ? (
                  !col.error && <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">No leads</div>
                ) : (
                  col.rows.map((c) => {
                    const reply = needsReply(c.last_comm);
                    const owner = c.owner_user_id ? byId.get(c.owner_user_id) : undefined;
                    return (
                      <div
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', c.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDrag({ id: c.id, from: l.value });
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          setDragOver(null);
                        }}
                        {...rowNav(`/crm/contacts/${c.id}`)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/crm/contacts/${c.id}`);
                          }
                        }}
                        className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50 ${
                          drag?.id === c.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <RowLink
                            to={`/crm/contacts/${c.id}`}
                            title={contactName(c)}
                            draggable={false}
                            className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}
                          >
                            {contactName(c)}
                          </RowLink>
                          {isActiveSnooze(c.snoozed_until) && (
                            <span className="flex-shrink-0" title="Snoozed — visible because Show snoozed is on">
                              <MoonStar className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                            </span>
                          )}
                          {c.archived_at && (
                            <span className="flex-shrink-0" title="Conversation archived">
                              <Archive className="h-3 w-3 text-gray-400" />
                            </span>
                          )}
                          {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                        </div>
                        {(c.studies || []).length > 0 && (
                          <div className="mt-1.5">
                            <StudyChips studies={c.studies} max={1} />
                          </div>
                        )}
                        {c.milestone && (
                          <div className="mt-1.5">
                            <MilestoneChip
                              milestone={c.milestone}
                              hideKeys={COLUMN_MILESTONE[l.value] ? [COLUMN_MILESTONE[l.value]] : undefined}
                            />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                            {c.last_comm ? (
                              <>
                                <ChannelIcon channel={c.last_comm.channel} />
                                {relTime(c.last_comm.occurred_at)}
                              </>
                            ) : (
                              relTime(c.created_at)
                            )}
                          </span>
                          {c.owner_user_id && (
                            <span
                              title={owner?.name || 'Assigned'}
                              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                            >
                              {initials(owner?.name)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {!col.loading && col.hasMore && (
                  <button
                    onClick={() => fetchColumn(l.value, col.fetchedCount, seqRef.current, true)}
                    disabled={col.loadingMore}
                    className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    {col.loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {col.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Drag cards between stages, or open a lead to change its status.
      </p>
    </div>
  );
}
